/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormGroup, Input } from 'reactstrap';
import {
  useEnableDataIntegrationMutation,
  useGetDataIntegrationStatusQuery,
} from '../../../../api/initiative-data-integrations';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { addSiteAlert, addSiteError, SiteAlertColors } from '../../../../slice/siteAlertsSlice';
import { generateUrl } from '../../../../routes/util';
import { ROUTES } from '../../../../constants/routes';
import { BlockingLoader } from '../../../../atoms/loader';
import { FeaturePermissions } from '../../../../services/permissions/FeaturePermissions';
import { useAppSelector } from '../../../../reducers';
import { ExtraFeature, FeatureStability } from '@g17eco/molecules/feature-stability';
import { DataIntegrations, DataIntegrationsTab } from './components/DataIntegrations';

export const DataIntegrationsRoute = () => {
  const { initiativeId, tab: activeTab = DataIntegrationsTab.Connections } = useParams<{ initiativeId: string; tab?: DataIntegrationsTab }>();

  const canAccessDataIntegrations = useAppSelector(FeaturePermissions.canAccessDataIntegrations);
  const history = useHistory();
  const [enableDataIntegration, { isLoading: isDataIntegrationEnabling }] = useEnableDataIntegrationMutation();
  const { data: dataIntegrationStatus } = useGetDataIntegrationStatusQuery({
    initiativeId,
  });

  const isDataIntegrationEnabled = dataIntegrationStatus?.enabled;

  const handleTabChange = (tab: DataIntegrationsTab) => {
    history.push(generateUrl(ROUTES.DATA_INTEGRATIONS, { initiativeId, tab }));
  };

  const handleEnableDataIntegration = async () => {
    try {
      await enableDataIntegration({ initiativeId }).unwrap();
      addSiteAlert({
        content: 'Data integration service has been enabled successfully',
        color: SiteAlertColors.Success,
      });
    } catch (error) {
      addSiteError(error);
    }
  };

  if (isDataIntegrationEnabled === undefined) {
    return <BlockingLoader />;
  }

  return (
    <Dashboard>
      <DashboardSectionTitle
        title={
          <>
            AI Data Source Integrations <FeatureStability feature={ExtraFeature.DataIntegrations} />
          </>
        }
        subtitle='Connect your data sources to the G17Eco platform, enhanced using AI semantic modelling and querying.'
        buttons={[
          <div className='d-flex flex-column align-items-center'>
            <FormGroup switch className='mb-1'>
              <Input
                type='switch'
                className='form-control-sm'
                style={{ width: '3rem' }}
                checked={isDataIntegrationEnabled}
                disabled={!canAccessDataIntegrations || isDataIntegrationEnabling || isDataIntegrationEnabled}
                onChange={handleEnableDataIntegration}
              />
            </FormGroup>
            <span className={'text-sm text-secondary'}>
              {isDataIntegrationEnabling ? <i className='fal fa-spinner fa-spin me-2' /> : null}
              {isDataIntegrationEnabling ? 'Enabling...' : !isDataIntegrationEnabled ? 'DISABLED' : 'ENABLED'}
            </span>
          </div>,
        ]}
      />

      <DashboardSection>
        <DataIntegrations
          initiativeId={initiativeId}
          handleTabChange={handleTabChange}
          activeTab={activeTab}
        />
      </DashboardSection>

      <DashboardRow>
        <div className='px-3 text-muted'>
          <div className='d-flex align-items-start'>
            <i className='fal fa-info-circle me-2 mt-1' />
            <div>
              <strong>AI-Assisted Semantic Modeling</strong>
              <p>
                Our AI-powered semantic layer uses AI to help you understand and model your data. Ask questions in
                natural language to explore your data schema and generate semantic models. The system analyzes your data
                structure and relationships to provide intelligent suggestions.
              </p>
              <p className='text-muted'>
                The analyser processes metadata like table schemas and relationships -{' '}
                <strong>not the underlying data values</strong>. This enables automated semantic modeling while
                maintaining data privacy and security.
              </p>
            </div>
          </div>
          <div className='d-flex align-items-start'>
            <i className='fal fa-info-circle me-2 mt-1' />
            <div>
              <strong>Data Residency</strong>
              <p className='mb-1'>
                Connecting to external data sources will enable WWG to process your data for synchronization. This may
                involve data transfer outside your current region while in transit, but your data will not be stored off
                region.
              </p>
              <p className='text-muted'>
                Ensure you have appropriate authorization and that this aligns with your data governance policies.
              </p>
            </div>
          </div>
        </div>
      </DashboardRow>
    </Dashboard>
  );
};
